// ── Participants ("/participants") — the admin roster (Wave 4) ──────────────
// ONE honest read (the participants list) routed through useHonestQuery ->
// <HonestState>. The page NEVER hand-rolls state branching: a gate / permission
// -denied / unavailable / error response renders via <HonestState> (tagged
// data-honest-posture), NEVER as a plain empty table. Real content (the
// DataTable + filter/create toolbar) renders only on the ready-with-data (or
// honest-empty) path.
//
// ── PARTICIPANT ISOLATION (blueprint §6 gate #1, FR-PDASH-001) ──────────────
// The ADMIN participants module is for STAFF / coach / company_admin /
// cohort_lead ONLY. A `participant` is DENIED the module entirely:
//   - the route manifest's `participants` route uses operationsRoles, which
//     EXCLUDES participant + cohort_lead at the ROUTE level (generic-not-found +
//     no Participants nav item) — proven in the tests;
//   - THIS page ALSO fail-closes (canAccessParticipantsModule) so a participant
//     who somehow reached the component renders ONLY permission-denied with NO
//     roster — they never see another participant's record through this module.
//     (Participant own-profile context is the Wave-9 Participant Dashboard.)
//
// ROLE-GATING of the affordances:
//   - Super Admin / Admin -> full CRUD: Add + Edit + Remove (participants:create/
//     update). The server re-applies scope.
//   - Coach -> SCOPED VIEW plus Add for assigned cohorts. The server re-applies
//     cohort scope and the Week-3 cutoff; a cutoff refusal stays honest in the
//     create modal (PA-02 / TC-101).
//   - Company Admin -> company-scoped READ (no write affordances).
//   - Cohort Lead -> assigned-cohort READ (no write affordances).
// Affordances render ONLY when the read is READY (posture null) or honest-EMPTY;
// a hard-denied read shows ONLY the honest state.

import { Button, CollectionToolbar, IconUsers, PageFrame, ProTable, useToast } from "@vjal/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { HonestState } from "../../components/honest-state";
import { honestGateFromError, postureForCoachingError, referenceFromError } from "../../services/coaching-error-posture";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { useParticipantCreateMutation } from "../../hooks/useCoachingQueries";
import { useParticipantLifecycleMutations } from "../../hooks/useParticipantLifecycleMutations";
import { evaluateActionCapability } from "../../domain/capabilities";
import { asLiveApiId } from "../../hooks/useLiveProductData";
import { useWalkReadinessHold } from "../../lib/walk-readiness";
import type { CoachingCohortDto, CoachingOperationResult, CoachingParticipantDto } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import { ParticipantFormModal, type ParticipantFormValues } from "./participant-form-modal";
import { ParticipantRemoveModal } from "./participant-remove-modal";
import { canAccessParticipantsModule } from "./participants-helpers";
import {
  cohortOptionsFromRows,
  useParticipantsCollection,
  type CohortFilterOption,
  type ParticipantsCollectionFetcher
} from "./participants-collection";

const services = getCoachingServices();

export function ParticipantsPage({ role, routePath }: ProductScreenContext) {
  const toast = useToast();
  const create = useParticipantCreateMutation();
  const lifecycle = useParticipantLifecycleMutations();

  const allowCreate = evaluateActionCapability(role, "participants:create").allowed;
  const allowUpdate = evaluateActionCapability(role, "participants:update").allowed;

  // ── Fail-closed module guard (participant isolation) ────────────────────────
  // Deny BEFORE any read fires (the read is also gated below via `enabled`) so no
  // roster request is even made for a denied role.
  const moduleDenied = !canAccessParticipantsModule(role);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CoachingParticipantDto | null>(null);
  const [removeTarget, setRemoveTarget] = useState<CoachingParticipantDto | null>(null);
  const [rowCohortOptions, setRowCohortOptions] = useState<CohortFilterOption[]>([]);
  const [confirmedCreateRows, setConfirmedCreateRows] = useState<Array<CoachingParticipantDto & Record<string, unknown>>>([]);
  const [confirmedRemovedIds, setConfirmedRemovedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [initialRosterReadPending, setInitialRosterReadPending] = useState(true);

  // The cohort list is an authenticated, server-scoped read: coaches receive
  // only cohorts assigned to them (the same scope that constrains participant
  // roster rows). Never widen the selector client-side.
  const cohortListQuery = useQuery({
    queryKey: ["coaching", "participants", "cohorts"],
    queryFn: () => services.cohorts?.list?.() ?? Promise.resolve({ items: [] }),
    enabled: !moduleDenied
  });
  useWalkReadinessHold(!moduleDenied && cohortListQuery.isLoading && !cohortListQuery.data && !cohortListQuery.isError);
  const cohortOptions = useMemo((): CohortFilterOption[] => {
    const options = (cohortListQuery.data?.items ?? [])
      .map((cohort: CoachingCohortDto) => ({
        id: (cohort.id ?? "").trim(),
        label: (cohort.name ?? "").trim() || (cohort.id ?? "").trim()
      }))
      .filter((option) => option.id.length > 0);
    return options;
  }, [cohortListQuery.data?.items]);
  const fetchParticipants = useCallback<ParticipantsCollectionFetcher>(
    async ({ page, pageSize, q, filters }) => {
      if (moduleDenied) return { rows: [], total: 0 };
      const response = await services.participants.list({
        page,
        pageSize,
        q: q.trim() || undefined,
        id: typeof filters.id === "string" ? filters.id : undefined,
        company_id: typeof filters.company_id === "string" ? filters.company_id : undefined,
        cohort_id: typeof filters.cohort_id === "string" ? filters.cohort_id : undefined,
        status: typeof filters.status === "string" ? filters.status : undefined,
        include_removed: "true"
      });
      const rows = response.items.map((row) => ({ ...row }));
      setRowCohortOptions(cohortOptionsFromRows(rows));
      return { rows, total: response.total ?? response.items.length };
    },
    [moduleDenied]
  );
  const collectionActions = useMemo(
    () => ({
      allowUpdate,
      onEdit: setEditTarget,
      onRemove: setRemoveTarget
    }),
    [allowUpdate]
  );
  const effectiveCohortOptions = cohortOptions.length > 0 ? cohortOptions : rowCohortOptions;
  const collection = useParticipantsCollection(fetchParticipants, collectionActions, effectiveCohortOptions, routePath);
  const formCohortOptions = effectiveCohortOptions;
  // A successful create response is authoritative write truth. Keep it visible
  // while the roster refetch catches up; this is not optimistic because the row
  // enters only from the resolved API response. A confirmed removal is write
  // truth too: the row is shown as removed right away instead of the pre-removal
  // row a refetch inside the read cache would still return.
  const collectionWithConfirmedRows = useMemo(() => {
    if (collection.uiState === "error") return collection;
    const existingIds = new Set(collection.rows.map((row) => row.id));
    const responseRows = confirmedCreateRows.filter((row) => !existingIds.has(row.id));
    if (responseRows.length === 0 && confirmedRemovedIds.size === 0) return collection;
    const markRemoved = (row: (typeof collection.rows)[number]) =>
      confirmedRemovedIds.has(row.id) ? { ...row, status: "removed" } : row;
    const rows = [...collection.rows, ...responseRows].map(markRemoved);
    return {
      ...collection,
      rows,
      allRows: [...collection.allRows, ...responseRows].map(markRemoved),
      total: Math.max(collection.total, rows.length),
      uiState: collection.uiState === "empty" && rows.length > 0 ? "ready" : collection.uiState
    } as typeof collection;
  }, [collection, confirmedCreateRows, confirmedRemovedIds]);
  useEffect(() => {
    if (collection.state.status === "success" || collection.state.status === "error") setInitialRosterReadPending(false);
  }, [collection.state.status]);
  useWalkReadinessHold(
    !moduleDenied &&
      initialRosterReadPending &&
      (collection.state.status === "idle" || collection.state.status === "loading")
  );

  // ── Fail closed: a denied role gets a permission-denied posture, NO read. ───
  if (moduleDenied) {
    return (
      <section aria-label="Participants" className="col-span-12" data-testid="participants-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture="permission-denied"
            title="You don't have access to the participants directory"
            message="The participants directory is for coaching staff. Your own details are available on your dashboard."
          />
        </div>
      </section>
    );
  }

  const hasScopedView = Boolean(collectionWithConfirmedRows.state.q.trim()) || collectionWithConfirmedRows.activeFilters.length > 0 || collectionWithConfirmedRows.state.page > 1;
  const noRowsOnScopedPage = hasScopedView && collectionWithConfirmedRows.uiState === "ready" && collectionWithConfirmedRows.rows.length === 0;
  const displayCollection = noRowsOnScopedPage
    ? ({ ...collectionWithConfirmedRows, uiState: "empty" } as typeof collectionWithConfirmedRows)
    : collectionWithConfirmedRows;
  const posture = collectionWithConfirmedRows.uiState === "loading" || collectionWithConfirmedRows.state.status === "idle"
    ? "loading"
    : collectionWithConfirmedRows.uiState === "error"
      ? postureForCoachingError(collectionWithConfirmedRows.state.error)
      : collectionWithConfirmedRows.total === 0 && !hasScopedView
        ? "empty"
        : null;
  const gated = posture !== null;
  const showToolbar = posture === null || posture === "empty";

  // ── Writes (each toasts ONLY after the backend confirms; no false success) ──
  // The row is shown only after the API resolves the create mutation. It is
  // response truth, not an optimistic placeholder; the refresh reconciles it
  // with the authoritative roster query.
  function submitCreate(values: ParticipantFormValues) {
    return create
      .mutateAsync({
        email: values.email,
        ...(values.name ? { name: values.name } : {}),
        cohortId: asLiveApiId(values.cohortId)
      })
      .then((participant) => {
        setConfirmedCreateRows((current) => [...current, { ...participant }]);
        collection.refresh();
        setCreateOpen(false);
        create.reset();
        toast.success(`Participant ${participant.name ?? values.email} added`);
        return participant;
      });
  }

  function submitEdit(values: ParticipantFormValues) {
    if (!editTarget) return Promise.reject(new Error("No participant selected"));
    const cohortId = editTarget.cohort_id;
    if (!cohortId) return Promise.reject(new Error("Participant has no cohort to update against"));
    // Preserve unchanged fields: send the (possibly edited) name + status; the
    // PATCH allows name/status. Email is identity-owned (read-only) and is never
    // sent. Unchanged values flow through verbatim from the pre-populated form.
    return lifecycle.update
      .mutateAsync({
        cohortId,
        participantId: editTarget.id,
        body: { name: values.name, status: values.status }
      })
      .then((participant) => {
        setEditTarget(null);
        lifecycle.update.reset();
        toast.success(`Participant ${participant.name ?? values.name ?? values.email} updated`);
        return participant;
      });
  }

  function submitRemove(): Promise<CoachingOperationResult> {
    if (!removeTarget) return Promise.reject(new Error("No participant selected"));
    const cohortId = removeTarget.cohort_id;
    if (!cohortId) return Promise.reject(new Error("Participant has no cohort to remove from"));
    return lifecycle.remove
      .mutateAsync({ cohortId, participantId: removeTarget.id })
      .then((result) => {
        // Honest: only toast + close on a confirmed removal (ok:true). A soft
        // refusal stays open showing the honest write message — no false success.
        if (result.ok) {
          const name = removeTarget.name || removeTarget.email;
          const removedId = removeTarget.id;
          setConfirmedRemovedIds((current) => new Set(current).add(removedId));
          setRemoveTarget(null);
          lifecycle.remove.reset();
          toast.success(`Participant ${name ?? ""} removed`.trim());
        }
        return result;
      });
  }

  return (
    <PageFrame title="Participants" subtitle="People enrolled in cohorts in your scope." className="col-span-12">
      <section aria-label="Participants" className="flex flex-col gap-4" data-testid="participants-page">
        {showToolbar && gated ? (
          <CollectionToolbar
            collection={collection}
            searchPlaceholder="Search by name or email…"
            primaryAction={
              allowCreate ? (
                <Button variant="primary" onClick={() => setCreateOpen(true)}>
                  Add participant
                </Button>
              ) : undefined
            }
            className="rounded-xl border border-line"
            stableChipNames
          />
        ) : null}

        <div className="rounded-xl border border-line bg-white">
          {gated ? (
            <HonestState
              posture={posture ?? "loading"}
              gate={honestGateFromError(collection.state.error)}
              reference={referenceFromError(collection.state.error)}
              onRetry={collection.refresh}
              icon={<IconUsers size={22} />}
              canCreate={allowCreate && !showToolbar}
              title={posture === "empty" ? "No participants yet" : undefined}
              message={
                posture === "empty"
                  ? allowCreate
                    ? "Add the first participant to a cohort to get started."
                    : "Participants will appear here once they are added to a cohort."
                  : undefined
              }
              createAction={
                allowCreate ? (
                  <Button variant="primary" onClick={() => setCreateOpen(true)}>
                    Add participant
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <ProTable
              collection={displayCollection}
              primaryAction={
                allowCreate ? (
                  <Button variant="primary" onClick={() => setCreateOpen(true)}>
                    Add participant
                  </Button>
                ) : undefined
              }
              className="p-0"
              tableProps={{
                caption: "Participants in the current view",
                emptyTitle: hasScopedView ? "No participants match your search" : "No participants yet",
                emptyMessage: hasScopedView
                  ? `No matching participants on this page. ${collection.total} total participant${collection.total === 1 ? "" : "s"} match the current filters.`
                  : "Add a participant to get started."
              }}
              stableChipNames
            />
          )}
        </div>

      {allowCreate && (
        <ParticipantFormModal
          open={createOpen}
          mode="create"
          cohortOptions={formCohortOptions}
          defaultCohortId={formCohortOptions.length === 1 ? formCohortOptions[0].id : undefined}
          onClose={() => {
            setCreateOpen(false);
            create.reset();
          }}
          onSubmit={submitCreate}
          submitting={create.isPending}
          submitError={create.error}
        />
      )}

      {allowUpdate && (
        <>
          <ParticipantFormModal
            open={editTarget !== null}
            mode="edit"
            participant={editTarget}
            onClose={() => {
              setEditTarget(null);
              lifecycle.update.reset();
            }}
            onSubmit={submitEdit}
            submitting={lifecycle.update.isPending}
            submitError={lifecycle.update.error}
          />
          <ParticipantRemoveModal
            open={removeTarget !== null}
            participant={removeTarget}
            onClose={() => {
              setRemoveTarget(null);
              lifecycle.remove.reset();
            }}
            onConfirm={submitRemove}
            submitting={lifecycle.remove.isPending}
            submitError={lifecycle.remove.error}
          />
        </>
      )}
      </section>
    </PageFrame>
  );
}
